import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card } from './common/Card';
import { Button } from './common/Button';

interface SeedStats {
  totalItemsInDatabase: number;
  availableItemsToSeed: number;
  itemsByCategory: { _id: string; count: number }[];
  canSeed: boolean;
}

const DatabaseSeeder: React.FC = () => {
  const [stats, setStats] = useState<SeedStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/seed/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching seed stats:', err);
      setError('Erreur lors du chargement des statistiques');
    }
  };

  const seedDatabase = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const response = await api.post('/seed/groceries');
      if (response.data.success) {
        setMessage(`✅ ${response.data.message}`);
        await fetchStats();
      } else {
        setError(response.data.message || 'Erreur lors de l\'ajout des articles');
      }
    } catch (err: any) {
      console.error('Error seeding database:', err);
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout des articles');
    } finally {
      setLoading(false);
    }
  };

  const seedFreshDatabase = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer tous les articles existants et les remplacer par la liste prédéfinie ?')) {
      return;
    }

    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const response = await api.post('/seed/fresh');
      if (response.data.success) {
        setMessage(`✅ ${response.data.message}`);
        await fetchStats();
      } else {
        setError(response.data.message || 'Erreur lors du remplacement des articles');
      }
    } catch (err: any) {
      console.error('Error seeding fresh database:', err);
      setError(err.response?.data?.message || 'Erreur lors du remplacement des articles');
    } finally {
      setLoading(false);
    }
  };

  const resetDatabase = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer TOUS les articles de la base de données ?')) {
      return;
    }

    setLoading(true);
    setMessage(null);
    setError(null);

    try {
      const response = await api.delete('/seed/reset');
      if (response.data.success) {
        setMessage(`✅ ${response.data.message}`);
        await fetchStats();
      } else {
        setError(response.data.message || 'Erreur lors de la suppression');
      }
    } catch (err: any) {
      console.error('Error resetting database:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setLoading(false);
    }
  };

  if (!stats) {
    return <div className="text-center text-gray-600">Chargement des statistiques...</div>;
  }

  return (
    <Card padding="lg" shadow="medium" className="mb-8">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">🌱 Articles prédéfinis</h4>

      <div className="mb-4 space-y-2">
        <p className="text-gray-700">
          <strong>Articles dans la base:</strong> {stats.totalItemsInDatabase}
        </p>
        <p className="text-gray-700">
          <strong>Articles disponibles pour l'import:</strong> {stats.availableItemsToSeed}
        </p>
      </div>

      {stats.itemsByCategory.length > 0 && (
        <div className="mb-4">
          <p className="font-semibold text-gray-900 mb-2">Répartition par catégorie:</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm text-gray-700">
            {stats.itemsByCategory.map(category => (
              <span key={category._id}>
                {category._id}: {category.count}
              </span>
            ))}
          </div>
        </div>
      )}

      {message && (
        <div className="bg-green-50 text-success border-l-4 border-success px-4 py-3 rounded-lg mb-4">
          {message}
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-danger border-l-4 border-danger px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {stats.canSeed && (
          <Button
            variant="primary"
            size="md"
            onClick={seedDatabase}
            disabled={loading}
          >
            {loading ? 'Ajout en cours...' : `➕ Ajouter ${stats.availableItemsToSeed} articles`}
          </Button>
        )}

        <Button
          variant="secondary"
          size="md"
          onClick={seedFreshDatabase}
          disabled={loading}
        >
          {loading ? 'Remplacement...' : '🔄 Remplacer par la liste complète'}
        </Button>

        <Button
          variant="danger"
          size="md"
          onClick={resetDatabase}
          disabled={loading}
        >
          {loading ? 'Suppression...' : '🗑️ Vider la base'}
        </Button>

        <Button
          variant="outline"
          size="md"
          onClick={fetchStats}
          disabled={loading}
        >
          🔄 Actualiser
        </Button>
      </div>
    </Card>
  );
};

export default DatabaseSeeder;